import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Search, Play, FileText, Download, Clock, Eye, TrendingUp, Star, ArrowRight, Mail } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import "@/styles/hub.css";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; }
}

export default function KnowledgeHub() {
  const [content, setContent] = useState(null);
  const [meta, setMeta] = useState({ sections: [], categories: {} });
  const [posts, setPosts] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [videos, setVideos] = useState([]);
  const [section, setSection] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/hub/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/hub/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/hub/trending").then((r) => setTrending(r.data)).catch(() => {});
    api.get("/hub/popular-videos").then((r) => setVideos(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (section) params.set("section", section);
    if (category) params.set("category", category);
    if (q) params.set("q", q);
    api.get(`/hub?${params.toString()}`).then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [section, category, q]);

  const allCats = useMemo(() => Object.entries(meta.categories || {}), [meta]);

  return (
    <div>
      <Header />
      <section className="bg-navy text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 grain-band opacity-30" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <span className="text-xs tracking-[0.25em] uppercase text-emerald-light font-medium">Knowledge Hub</span>
          <h1 className="mt-3 font-display font-bold text-4xl lg:text-5xl tracking-tight">Insights for Employers, HR &amp; Candidates</h1>
          <p className="mt-3 text-slate-300 max-w-2xl">Articles, videos and free resources on hiring, careers, HR and workforce trends — from the team at Ishaan Workforce Solutions.</p>
          <div className="relative max-w-md mt-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input data-testid="hub-search" placeholder="Search articles, videos, resources…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white text-slate-800 focus-visible:ring-emerald-brand" />
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto py-3">
          <button onClick={() => setSection("")} data-testid="hub-tab-all" className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${section === "" ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100"}`}>All</button>
          {(meta.sections || []).map((s) => (
            <button key={s.id} onClick={() => setSection(s.id)} data-testid={`hub-tab-${s.id}`} className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${section === s.id ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100"}`}>{s.label}</button>
          ))}
        </div>
      </section>

      {featured.length > 0 && !section && !q && !category && (
        <section className="py-10 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="font-display font-semibold text-navy text-xl flex items-center gap-2 mb-5"><Star size={18} className="text-amber-500" /> Featured</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {featured.slice(0, 3).map((p, i) => <PostCard key={p.id} p={p} i={i} big />)}
            </div>
          </div>
        </section>
      )}

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-4 gap-10">
          <div className="lg:col-span-3">
            {allCats.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                <button onClick={() => setCategory("")} className={`text-xs rounded-full px-3 py-1 border ${category === "" ? "bg-emerald-brand text-white border-emerald-brand" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>All topics</button>
                {allCats.flatMap(([, cats]) => cats).slice(0, 14).map((c) => (
                  <button key={c} onClick={() => setCategory(c)} data-testid={`hub-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={`text-xs rounded-full px-3 py-1 border transition-colors ${category === c ? "bg-emerald-brand text-white border-emerald-brand" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>{c}</button>
                ))}
              </div>
            )}
            {loading ? <p className="text-slate-500">Loading…</p> : posts.length === 0 ? (
              <div data-testid="hub-empty" className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500">Nothing here yet. Please check back soon.</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-6" data-testid="hub-grid">
                {posts.map((p, i) => <PostCard key={p.id} p={p} i={i} />)}
              </div>
            )}
          </div>

          <aside className="space-y-8">
            {trending.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="font-display font-semibold text-navy flex items-center gap-2 mb-4"><TrendingUp size={17} className="text-emerald-brand" /> Trending</h3>
                <div className="space-y-4">
                  {trending.map((p, i) => (
                    <Link key={p.id} to={`/hub/${p.slug}`} data-testid={`hub-trending-${i}`} className="flex gap-3 group">
                      <span className="font-display font-bold text-2xl text-slate-200 group-hover:text-emerald-brand transition-colors">{i + 1}</span>
                      <div><p className="text-sm font-medium text-navy leading-snug group-hover:text-emerald-brand transition-colors line-clamp-2">{p.title}</p><span className="text-xs text-slate-400">{p.reading_time || 1} min read</span></div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {videos.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="font-display font-semibold text-navy flex items-center gap-2 mb-4"><Play size={16} className="text-emerald-brand" /> Popular Videos</h3>
                <div className="space-y-3">
                  {videos.map((p, i) => (
                    <Link key={p.id} to={`/hub/${p.slug}`} data-testid={`hub-popvid-${i}`} className="flex gap-3 items-center group">
                      <div className="relative h-12 w-20 shrink-0 rounded-lg bg-navy overflow-hidden">
                        {p.video_thumbnail && <img src={p.video_thumbnail} alt="" className="h-full w-full object-cover opacity-80" />}
                        <Play size={16} className="absolute inset-0 m-auto text-white" />
                      </div>
                      <p className="text-sm font-medium text-navy leading-snug group-hover:text-emerald-brand transition-colors line-clamp-2">{p.title}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            <NewsletterBox />
          </aside>
        </div>
      </section>
      <Footer content={content} />
    </div>
  );
}

function PostCard({ p, i, big }) {
  const TI = p.type === "video" ? Play : p.type === "resource" ? Download : FileText;
  const img = p.type === "video" ? (p.video_thumbnail || p.cover_image) : p.cover_image;
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 6) * 0.05 }} data-testid={`hub-card-${i}`}>
      <Link to={`/hub/${p.slug}`} className="group block bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-full">
        <div className={`relative bg-navy/5 ${big ? "aspect-[16/9]" : "aspect-[16/10]"} overflow-hidden`}>
          {img ? <img src={img} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="h-full w-full flex items-center justify-center text-navy/20"><TI size={40} /></div>}
          {p.type === "video" && <div className="absolute inset-0 flex items-center justify-center"><span className="h-12 w-12 rounded-full bg-white/90 flex items-center justify-center"><Play size={20} className="text-emerald-brand ml-0.5" /></span></div>}
          {p.type === "video" && p.video_duration && <span className="absolute bottom-2 right-2 text-xs bg-black/70 text-white rounded px-1.5 py-0.5">{p.video_duration}</span>}
          {p.type === "resource" && <span className="absolute top-2 left-2 text-xs bg-emerald-brand text-white rounded-full px-2.5 py-1 flex items-center gap-1"><Download size={12} /> {p.resource_type || "Download"}</span>}
        </div>
        <div className="p-5">
          {p.category && <span className="text-xs font-medium text-emerald-brand">{p.category}</span>}
          <h3 className="mt-1.5 font-display font-semibold text-navy leading-snug group-hover:text-emerald-brand transition-colors line-clamp-2">{p.title}</h3>
          {p.excerpt && <p className="mt-2 text-sm text-slate-500 line-clamp-2">{p.excerpt}</p>}
          <div className="mt-4 flex items-center gap-4 text-xs text-slate-400">
            <span className="flex items-center gap-1"><Clock size={12} /> {p.reading_time || 1} min</span>
            <span className="flex items-center gap-1"><Eye size={12} /> {p.views || 0}</span>
            <span>{fmtDate(p.published_at)}</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export function NewsletterBox() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/newsletter", { email }); setDone(true); toast.success("Subscribed! Thank you."); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Please enter a valid email"); }
    finally { setLoading(false); }
  };
  return (
    <div className="bg-navy text-white rounded-2xl p-6 relative overflow-hidden" data-testid="hub-newsletter">
      <div className="absolute inset-0 grain-band opacity-20" />
      <div className="relative">
        <Mail size={22} className="text-emerald-light" />
        <h3 className="mt-3 font-display font-semibold text-lg">Get HR &amp; hiring insights</h3>
        <p className="mt-1 text-sm text-slate-300">New articles, resources and workforce trends — straight to your inbox.</p>
        {done ? (
          <p className="mt-4 text-sm text-emerald-light" data-testid="newsletter-success">You're subscribed 🎉</p>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-2">
            <Input required type="email" data-testid="newsletter-email" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white text-slate-800 focus-visible:ring-emerald-brand" />
            <Button type="submit" disabled={loading} data-testid="newsletter-submit" className="w-full bg-emerald-brand hover:bg-emerald-dark font-semibold">{loading ? "Subscribing…" : "Subscribe"}</Button>
          </form>
        )}
      </div>
    </div>
  );
}
